import {
  ChatListItem,
  ChatSearchFilters,
  ChatToggleData,
} from '@choco-admin/types';
import { mapEnabledFilterToBoolean } from '@choco-admin/utils';
import { useState, useEffect, useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';

import { useNotification } from '../../../contexts/NotificationContext';
import { usePuppyMode } from '../../../contexts/PuppyModeContext';
import { DEFAULT_PAGE_SIZE } from '../constants/chatConstants';

import { useChatToggle } from './useChatToggle';
import { useSearchChats } from './useSearchChats';

export interface UseChatListResponse {
  chats: ChatListItem[];
  totalChats: number;
  isLoading: boolean;
  currentPage: number;
  totalPages: number;
  searchTerm: string;
  filters: ChatSearchFilters;
  handleSearch: (term: string) => void;
  handleFilterChange: (filters: ChatSearchFilters) => void;
  handlePageChange: (page: number) => void;
  handleChatToggle: (data: ChatToggleData) => Promise<void>;
}

export const useChatList = (): UseChatListResponse => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [pageSize] = useState(DEFAULT_PAGE_SIZE);
  const { puppyFilter } = usePuppyMode();
  const [currentPage, setCurrentPage] = useState(1);

  // A different dogfood scope is a different result set, so go back to page 1
  // during render rather than querying the old offset first.
  const [lastPuppyFilter, setLastPuppyFilter] = useState(puppyFilter);
  if (lastPuppyFilter !== puppyFilter) {
    setLastPuppyFilter(puppyFilter);
    setCurrentPage(1);
  }
  const [searchTerm, setSearchTerm] = useState(
    searchParams.get('search') || ''
  );
  const [filters, setFilters] = useState<ChatSearchFilters>({
    type: searchParams.get('type') || 'all',
    // Default to showing enabled chats only; an explicit ?enabledState=all
    // (or =disabled) in the URL still takes precedence.
    enabledState:
      (searchParams.get('enabledState') as ChatSearchFilters['enabledState']) ||
      'enabled',
  });
  const { addNotification } = useNotification();

  const captureException = useCallback(() => {
    addNotification({
      title: 'Error Loading Chats',
      message: 'An error occurred while searching for chats. Please try again.',
      type: 'error',
    });
  }, [addNotification]);

  const searchConfig = useMemo(
    () => ({
      initialPageSize: DEFAULT_PAGE_SIZE,
      captureException,
    }),
    [captureException]
  );

  const { chats, totalChats, isLoading, searchChats } =
    useSearchChats(searchConfig);

  const { toggleChat } = useChatToggle();

  // Calculate pagination
  const totalPages = Math.ceil(totalChats / pageSize);

  // Update URL when search term or filters change (functional updater avoids searchParams dep loop)
  useEffect(() => {
    setSearchParams((prev) => {
      const params = new URLSearchParams(prev);
      if (searchTerm) {
        params.set('search', searchTerm);
      } else {
        params.delete('search');
      }
      if (filters.type && filters.type !== 'all') {
        params.set('type', filters.type);
      } else {
        params.delete('type');
      }
      // 'enabled' is the default, so it stays out of the URL; explicit
      // 'all'/'disabled' selections are persisted.
      if (filters.enabledState && filters.enabledState !== 'enabled') {
        params.set('enabledState', filters.enabledState);
      } else {
        params.delete('enabledState');
      }
      return params;
    });
  }, [searchTerm, filters, setSearchParams]);

  // Initial load and search effect
  useEffect(() => {
    const enabledFilter = mapEnabledFilterToBoolean(
      filters.enabledState || 'enabled'
    );

    const queryParams = {
      searchTerm,
      filter: {
        ...(filters.type !== 'all' && { type: filters.type }),
        ...(enabledFilter !== undefined && { enabled: enabledFilter }),
        ...(puppyFilter !== undefined && { isPuppy: puppyFilter }),
      },
      page: currentPage,
      pageSize,
    };

    // Debug logging (temporary)
    // console.log('Chat search params:', {
    //   searchTerm,
    //   filters,
    //   enabledFilter,
    //   queryParams
    // });

    searchChats(queryParams);
  }, [searchTerm, filters, currentPage, pageSize, searchChats, puppyFilter]);

  const handleSearch = useCallback((term: string) => {
    setSearchTerm(term);
    setCurrentPage(1);
  }, []);

  const handleFilterChange = useCallback((newFilters: ChatSearchFilters) => {
    setFilters((prev) => ({ ...prev, ...newFilters }));
    setCurrentPage(1);
  }, []);

  const handlePageChange = useCallback((page: number) => {
    setCurrentPage(page);
  }, []);

  const handleChatToggle = useCallback(
    async (data: ChatToggleData) => {
      // Cache is updated automatically by useUpdateChatEnabled on success and
      // error toasts are raised in useChatToggle; the error is re-thrown so
      // callers (e.g. the table's optimistic Switch) can revert their state.
      await toggleChat(data);
    },
    [toggleChat]
  );

  return {
    chats,
    totalChats,
    isLoading,
    currentPage,
    totalPages,
    searchTerm,
    filters,
    handleSearch,
    handleFilterChange,
    handlePageChange,
    handleChatToggle,
  };
};
